import { Alert } from "@mantine/core";
import { useEffect, useState } from "react";
import type { Control, FieldErrors } from "react-hook-form";
import { AlertPresets } from "../../../../../../../mantine/mantinePresets";
import urls from "../../../../../../../urls";
import { AnchorLink } from "../../../../../../AnchorLink/AnchorLink";
import type {
  FormFieldData,
  FormInputData
} from "../../../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormFieldController } from "../../../../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { FormSection } from "../../../../../../BuildingBlocks/Forms/FormSection/FormSection";
import { isFieldError } from "../../../../../../BuildingBlocks/Forms/MantineFormFields/HelperText/utils/isFieldError";
import { ChangeablePricesField } from "../../../../SharedFields/ChangeablePricesField";
import type { GridFees } from "../../../DeliveryModal.types";
import type { GridUsageBillingModel } from "../../../hooks/useGridFees";
import { useGridFees } from "../../../hooks/useGridFees";
import { DELIVERY_FORM_HELP_TEXTS } from "../../DeliveryForm.constants";
import type { DeliveryFormFields } from "../../DeliveryForm.types";

interface GridUsageFieldsWithHeaderProps {
  control: Control<DeliveryFormFields>;
  errors: FieldErrors<DeliveryFormFields>;
  formFieldData: FormFieldData<DeliveryFormFields>;
  automaticallyDetermineGridFeesValue: boolean;
  setAutomaticallyDetermineGridFeesValue: (value: boolean) => void;
  gridUsageBillingModelValue: string;
  siteId?: number | null;
}

function GridUsageFieldsWithHeader({
  control,
  errors,
  formFieldData,
  gridUsageBillingModelValue,
  automaticallyDetermineGridFeesValue,
  setAutomaticallyDetermineGridFeesValue,
  siteId
}: GridUsageFieldsWithHeaderProps) {
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [previewGridFees, setPreviewGridFees] = useState<GridFees | null>(null);

  const { gridFeesLoadingError, gridFees } = useGridFees(
    siteId,
    gridUsageBillingModelValue as GridUsageBillingModel,
    { enabled: automaticallyDetermineGridFeesValue }
  );

  useEffect(() => {
    if (automaticallyDetermineGridFeesValue && siteId) {
      if (gridFees) {
        setPreviewGridFees(gridFees);
      } else if (gridFeesLoadingError) {
        setPreviewGridFees(null);
        setShowErrorMessage(true);
        setAutomaticallyDetermineGridFeesValue(false);
      }
    }
  }, [
    automaticallyDetermineGridFeesValue,
    gridFees,
    gridFeesLoadingError,
    setAutomaticallyDetermineGridFeesValue,
    siteId
  ]);
  return (
    <FormSection
      className="GridUsageFieldsWithHeader"
      label="Netznutzungsentgelte"
      mt="xl"
    >
      <FormFieldController
        control={control}
        data={
          formFieldData.gridUsageBillingModel as FormInputData<
            DeliveryFormFields,
            "gridUsageBillingModel"
          >
        }
        error={errors.gridUsageBillingModel}
      />

      {(gridUsageBillingModelValue === "slp" ||
        gridUsageBillingModelValue === "rlm") && (
        <div className="grid-usage-fields-with-header-grid-fees-checkbox">
          <FormFieldController
            control={control}
            data={
              formFieldData.automaticallyDetermineGridFees as FormInputData<
                DeliveryFormFields,
                "automaticallyDetermineGridFees"
              >
            }
            error={errors.automaticallyDetermineGridFees}
          />
        </div>
      )}
      {showErrorMessage && (
        <Alert
          className="grid-usage-error-message-alert"
          color={AlertPresets.error.color}
          icon={AlertPresets.error.icon}
          withCloseButton
          onClose={() => setShowErrorMessage(false)}
        >
          <div>
            Für das aktuelle Jahr kann der Preis für Netznutzungsentgelte nicht
            automatisch ermittelt werden. Bitte kontaktieren Sie unseren{" "}
            <AnchorLink href={urls.optiSupport()}>Support</AnchorLink> oder
            geben Sie die Informationen manuell ein.
          </div>
        </Alert>
      )}

      {(gridUsageBillingModelValue === "slp" ||
        gridUsageBillingModelValue === "rlm") &&
        !automaticallyDetermineGridFeesValue && (
          <ChangeablePricesField
            className="WorkingPricesPrognosis"
            control={control}
            error={
              isFieldError(errors.workingPricesPrognosis)
                ? errors.workingPricesPrognosis
                : undefined
            }
            helpText={DELIVERY_FORM_HELP_TEXTS.workingPricesPrognosis}
            label="Arbeitspreis Netznutzung (Prognose)*"
            name="workingPricesPrognosis"
            unit="ct/kWh"
          />
        )}

      {gridUsageBillingModelValue === "rlm" &&
        !automaticallyDetermineGridFeesValue && (
          <ChangeablePricesField
            className="GridUsagePowerPrices"
            control={control}
            error={
              isFieldError(errors.gridUsagePowerPrices)
                ? errors.gridUsagePowerPrices
                : undefined
            }
            helpText={DELIVERY_FORM_HELP_TEXTS.gridUsagePowerPrices}
            label="Leistungspreis Netznutzung [€/kW]*"
            name="gridUsagePowerPrices"
            unit="€/kW"
          />
        )}

      {automaticallyDetermineGridFeesValue &&
        previewGridFees &&
        previewGridFees.energy_price && (
          <ChangeablePricesField
            allowInput={false}
            className="WorkingPricesPrognosis"
            collapsible={false}
            control={control}
            error={
              isFieldError(errors.workingPricesPrognosis)
                ? errors.workingPricesPrognosis
                : undefined
            }
            helpText={DELIVERY_FORM_HELP_TEXTS.workingPricesPrognosis}
            label="Arbeitspreis Netznutzung (Prognose)*"
            name="workingPricesPrognosis"
            previewValue={previewGridFees.energy_price}
            unit="ct/kWh"
          />
        )}

      {automaticallyDetermineGridFeesValue &&
        previewGridFees &&
        previewGridFees.power_price && (
          <ChangeablePricesField
            allowInput={false}
            className="GridUsagePowerPrices"
            collapsible={false}
            control={control}
            error={
              isFieldError(errors.gridUsagePowerPrices)
                ? errors.gridUsagePowerPrices
                : undefined
            }
            helpText={DELIVERY_FORM_HELP_TEXTS.gridUsagePowerPrices}
            label="Leistungspreis Netznutzung [€/kW]*"
            name="gridUsagePowerPrices"
            previewValue={previewGridFees.power_price}
            unit="€/kW"
          />
        )}

      <FormFieldController
        control={control}
        data={
          formFieldData.lossesTransfer as FormInputData<
            DeliveryFormFields,
            "lossesTransfer"
          >
        }
        error={errors.lossesTransfer}
      />
    </FormSection>
  );
}

export { GridUsageFieldsWithHeader };
